import * as THREE from 'three';

/**
 * BreedingSystem — Manages animal breeding.
 *
 * Feed 2 animals of same type with right food = baby animal.
 *
 * Breeding foods:
 *   PIG: ItemId.SEEDS (or carrot/potato but we don't have those as separate items — use SEEDS)
 *   COW: ItemId.WHEAT_ITEM
 *   SHEEP: ItemId.WHEAT_ITEM
 *   CHICKEN: ItemId.SEEDS
 *
 * Mechanics:
 * - Right-click animal with correct food = animal enters "love mode" (heart particles, 30s timeout)
 * - Two animals of same type in love mode within 5 blocks = breed
 * - Baby spawns between parents, 50% scale
 * - Baby grows to adult in 5 minutes (300 seconds)
 * - Parents have 5 min cooldown before breeding again
 * - Player gets 1-7 XP on successful breeding
 */
export class BreedingSystem {
  constructor(entityManager, scene) {
    this.entityManager = entityManager;
    this.scene = scene;
    this._loveMode = new Map();  // entityRef -> { timer, type }
    this._cooldowns = new Map(); // entityRef -> cooldownTimer
    this._babies = [];           // { entity, growTimer }
  }

  // Get the breeding food for a mob type. Returns ItemId or null.
  getBreedingFood(mobType) {
    const FOODS = {
      PIG: -37,     // ItemId.SEEDS
      COW: -36,     // ItemId.WHEAT_ITEM
      SHEEP: -36,   // ItemId.WHEAT_ITEM
      CHICKEN: -37, // ItemId.SEEDS
      HORSE: -92,   // ItemId.CARROT_ITEM
    };
    return FOODS[mobType] || null;
  }

  // Can this mob type be bred?
  canBreed(mobType) {
    return ['PIG', 'COW', 'SHEEP', 'CHICKEN', 'HORSE'].includes(mobType);
  }

  // Try to feed an animal. Returns true if accepted (enters love mode).
  tryFeed(entity, itemId) {
    if (!this.canBreed(entity.type)) return false;
    if (this.getBreedingFood(entity.type) !== itemId) return false;
    if (this._cooldowns.has(entity) && this._cooldowns.get(entity) > 0) return false;
    if (this._loveMode.has(entity)) return false; // already in love

    this._loveMode.set(entity, { timer: 30, type: entity.type });
    return true;
  }

  // Update: check for matching pairs, grow babies, tick timers
  update(dt, playerPos) {
    // result object
    const result = { bred: false, xp: 0, babyPos: null };

    // Tick love mode timers, remove expired
    for (const [entity, data] of this._loveMode) {
      data.timer -= dt;
      if (data.timer <= 0) this._loveMode.delete(entity);
    }

    // Tick cooldowns
    for (const [entity, time] of this._cooldowns) {
      const newTime = time - dt;
      if (newTime <= 0) this._cooldowns.delete(entity);
      else this._cooldowns.set(entity, newTime);
    }

    // Check for breeding pairs
    const loveEntities = [...this._loveMode.entries()];
    const paired = new Set();

    for (let i = 0; i < loveEntities.length; i++) {
      if (paired.has(i)) continue;
      const [entA, dataA] = loveEntities[i];

      for (let j = i + 1; j < loveEntities.length; j++) {
        if (paired.has(j)) continue;
        const [entB, dataB] = loveEntities[j];

        if (dataA.type !== dataB.type) continue;
        const dist = entA.position.distanceTo(entB.position);
        if (dist > 5) continue;

        // Breed!
        paired.add(i);
        paired.add(j);
        this._loveMode.delete(entA);
        this._loveMode.delete(entB);
        this._cooldowns.set(entA, 300); // 5 min cooldown
        this._cooldowns.set(entB, 300);

        // Spawn baby at midpoint
        const babyPos = entA.position.clone().add(entB.position).multiplyScalar(0.5);
        babyPos.y = Math.max(entA.position.y, entB.position.y);

        result.bred = true;
        result.xp = 1 + Math.floor(Math.random() * 7);
        result.babyPos = babyPos;
        result.babyType = dataA.type;

        break; // one breed per frame
      }
      if (result.bred) break;
    }

    // Grow babies
    for (let i = this._babies.length - 1; i >= 0; i--) {
      const baby = this._babies[i];
      baby.growTimer -= dt;

      // Gradually scale up
      const progress = 1 - (baby.growTimer / 300);
      const scale = 0.5 + progress * 0.5; // 0.5 → 1.0
      if (baby.entity.mesh) {
        baby.entity.mesh.scale.setScalar(scale);
      }

      if (baby.growTimer <= 0) {
        // Fully grown
        if (baby.entity.mesh) baby.entity.mesh.scale.setScalar(1);
        this._babies.splice(i, 1);
      }
    }

    return result;
  }

  // Register a newly spawned baby (called from main.js after spawning the entity)
  registerBaby(entity) {
    if (entity.mesh) entity.mesh.scale.setScalar(0.5);
    this._babies.push({ entity, growTimer: 300 }); // 5 minutes
  }

  // Check if entity is in love mode (for rendering hearts)
  isInLove(entity) {
    return this._loveMode.has(entity);
  }
}
